import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ActiveCartFacade } from '@spartacus/cart/base/root';
import { CheckoutStepService } from '@spartacus/checkout/base/components';
import { CheckoutDeliveryAddressFacade } from '@spartacus/checkout/base/root';
import {
  Address,
  AddressValidation,
  Country,
  GlobalMessageService,
  OCC_USER_ID_ANONYMOUS,
  OccEndpointsService, QueryState, Region,
  User,
  UserAddressService,
  UserIdService,
  UserPaymentService, WindowRef, getLastValueSync
} from '@spartacus/core';
import { LAUNCH_CALLER, LaunchDialogService } from '@spartacus/storefront';
import { UserAccountFacade } from '@spartacus/user/account/root';
import { BehaviorSubject, Observable, Subscription, combineLatest } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { headers } from 'src/app/spartacus/custom/core/occ/adapters/checkout/custom-occ-checkout-payment.adapter';
import {
  CustomCheckoutDeliveryAddressService
} from 'src/app/spartacus/custom/core/src/checkout/facade/custom-checkout-delivery-address.service';
import {
  CustomCheckoutPaymentService
} from 'src/app/spartacus/custom/core/src/checkout/facade/custom-checkout-payment.service';
import { CustomCart } from 'src/app/spartacus/custom/model/cart.model';
import { CustomValidators } from 'src/app/spartacus/custom/shared/utils/validators/custom-validators';
import { ICON_TYPE } from '../../../../../../../custom/icons/icon.model';


@Component({
  selector: 'cx-payment-form',
  templateUrl: './custom-payment-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class CustomPaymentFormComponent implements OnInit, OnDestroy {
  protected subscriptions: Subscription = new Subscription();
  addressBillingForm: QueryState<Address>;
  getBilling = false;
  iconTypes = ICON_TYPE;
  billingAddressForm: UntypedFormGroup;
  private addressVerifySub: Subscription;
  months: string[] = [];
  paymentDetails: QueryState<Address>;
  shippingAddress$: Observable<Address | undefined>;
  countries$: Observable<Country[]>;
  sameAsShippingAddress = false;
  isProfessional = false;
  regions$: Observable<Region[]>;
  selectedCountry$: BehaviorSubject<string> = new BehaviorSubject<string>('');
  showSameAsShippingAddressCheckbox$: Observable<boolean>;
  user: User;
  user$: Observable<User | undefined> = this.userAccount.get();
  guestEmail: string;
  currentName: string;
  currentNIE: string;
  currentAddress: string;
  currentPhone: string;
  currentCP: string;
  currentTown: string;
  currentRegion: string;
  cart$: Observable<CustomCart> = this.activeCartService.getActive();
  professional = false;

  @Input()
  setAsDefaultField: boolean;

  @Input()
  paymentMethodsCount: number;

  @Output()
  goBack = new EventEmitter<any>();

  @Output()
  closeForm = new EventEmitter<any>();

  @Output()
  setPaymentDetails = new EventEmitter<any>();

  userId: string;

  constructor(
    protected checkoutPaymentService: CustomCheckoutPaymentService,
    protected userPaymentService: UserPaymentService,
    protected globalMessageService: GlobalMessageService,
    protected fb: UntypedFormBuilder,
    protected launchDialogService: LaunchDialogService,
    protected userAddressService: UserAddressService,
    protected customCheckoutDeliveryAddressService: CustomCheckoutDeliveryAddressService,
    protected checkoutStepService: CheckoutStepService,
    protected activatedRoute: ActivatedRoute,
    protected userAccount: UserAccountFacade,
    protected activeCartService: ActiveCartFacade,
    protected checkoutDeliveryAddressFacade: CheckoutDeliveryAddressFacade,
    protected winRef: WindowRef,
    protected http: HttpClient,
    protected occEndpoints: OccEndpointsService,
    protected userIdService: UserIdService,
  ) {
    this.userIdService.takeUserId()
      .subscribe(userID => this.userId = userID)
      .unsubscribe();
    this.subscriptions.add(this.cart$?.subscribe(response => {
      this.guestEmail = response?.customer?.contactEmail;
      this.currentName = response?.paymentAddress?.firstName;
      this.currentNIE = response?.paymentAddress?.documentIdentifier;
      this.currentAddress = response?.paymentAddress?.line1;
      this.currentPhone = response?.paymentAddress?.cellphone;
      this.currentCP = response?.paymentAddress?.postalCode;
      this.currentTown = response?.paymentAddress?.town;
      this.currentRegion = response?.paymentAddress?.region.isocode;
    }));
    this.subscriptions.add(this.user$.subscribe(user => this.user = user));
    this.billingAddressForm = this.fb.group({
      firstName: [this.currentName || this.user?.commercialName, Validators.required],
      lastName: [''],
      line1: [this.currentAddress || this.user?.defaultBillingAddress?.line1, Validators.required],
      line2: [''],
      town: [this.currentTown || this.user?.defaultBillingAddress?.town, Validators.required],
      email: [this.guestEmail || this.user?.displayUid, [Validators.required, CustomValidators.emailValidator]],
      phone: [this.currentPhone || this.user?.defaultBillingAddress?.phone, [Validators.required, CustomValidators.phoneValidator]],
      documentIdentifier: [this.currentNIE || this.user?.documentIdentifier, [CustomValidators.documentIdentifier(this.isProfessional ? 'ohgarcustomergroup' : 'ohgarprofessionalgroup', 'ES')]],
      country: this.fb.group({
        isocode: ['ES', Validators.required],
      }),
      postalCode: [this.currentCP || this.user?.defaultBillingAddress?.postalCode, [
        Validators.required,
        Validators.minLength(5),
        Validators.maxLength(5),
        CustomValidators.onlyNumbers,
      ]],
      region: this.fb.group({
        isocode: [this.currentRegion || (this.user?.defaultBillingAddress?.region?.isocode ? this.user?.defaultBillingAddress?.region?.isocode : ''), [Validators.required, CustomValidators.regionCodeStartsWith('postalCode')]],
      }),
      billing: [this.user?.billing],
      sameAsShippingAddress: [''],
    });
    this.validatePostalCode();
    this.subscriptions.add(combineLatest([
      this.checkoutDeliveryAddressFacade.getDeliveryAddressState(),
      this.checkoutPaymentService.getPaymentDetailsState()
    ]).subscribe(([deliveryAddress, paymentDetails]) => {
      this.addressBillingForm = deliveryAddress;
      this.isProfessional = this.addressBillingForm?.data?.professional;
      this.paymentDetails = paymentDetails;
      if (this.paymentDetails?.data?.id !== undefined) {
        this.billingAddressForm.patchValue(this.paymentDetails);
      } else {
        if (this.addressBillingForm?.data?.sameAsShippingAddress) {
          this.toggleSameAsShippingAddress();
        }
      }
    }));

    this.selectedCountry$.next('ES');
  }

  toggleSameAsShippingAddress(): void {
    this.sameAsShippingAddress = !this.sameAsShippingAddress;
    if (this.sameAsShippingAddress) {
      this.billingAddressForm.patchValue(this.addressBillingForm.data);
    }
  }

  toggleGetBillingAddress(): void {
    if (!this.getBilling) {
      this.billingAddressForm.get('documentIdentifier').setValue('');
      this.billingAddressForm.get('documentIdentifier').setValidators([Validators.required, CustomValidators.documentIdentifier(this.isProfessional ? 'ohgarcustomergroup' : 'ohgarprofessionalgroup', 'ES')]);
      this.billingAddressForm.get('documentIdentifier').updateValueAndValidity();
    } else {
      this.billingAddressForm.get('documentIdentifier').setValue('');
      this.billingAddressForm.get('documentIdentifier').setValidators([CustomValidators.documentIdentifier(this.isProfessional ? 'ohgarcustomergroup' : 'ohgarprofessionalgroup', 'ES')]);
      this.billingAddressForm.get('documentIdentifier').updateValueAndValidity();
      this.billingAddressForm.get('firstName').setValidators([Validators.required, Validators.maxLength(70)]);
      this.billingAddressForm.get('firstName').updateValueAndValidity();
    }
    this.getBilling = !this.getBilling;
  }

  isProfessionalCheckout(): boolean {
    this.professional = this.user?.professional;
    return this.professional;
  }

  validForm(): boolean {
    return this.billingAddressForm.valid;
  }

  validatePostalCode(): void {
    this.billingAddressForm.get('region.isocode').updateValueAndValidity();
  }

  changeAetValidators(): void {
    if (this.getBilling) {
      this.billingAddressForm.get('documentIdentifier').setValidators([Validators.required, CustomValidators.documentIdentifier('ohgarprofessionalgroup', 'ES')]);
      this.billingAddressForm.get('documentIdentifier').updateValueAndValidity();
    }
    this.billingAddressForm.get('firstName').setValidators([Validators.required, Validators.maxLength(70)]);
    this.billingAddressForm.get('firstName').updateValueAndValidity();
  }

  protected getCartEndpoint(userId: string): string {
    const cartEndpoint = 'users/' + userId + '/carts/';
    return this.occEndpoints.buildUrl(cartEndpoint);
  }

  next(): void {
    if (this.validForm()) {
      const cart = getLastValueSync(this.activeCartService.getActive());
      let cartcode = cart.code;
      const address = this.billingAddressForm.value;
      if (this.userId === OCC_USER_ID_ANONYMOUS) {
        cartcode = cart.guid;
      }
      const url = this.getCartEndpoint(this.userId) + cartcode + '/addresses/payment';
      this.subscriptions.add(this.http.post<any>(url, { ...address }, { headers }).subscribe({
        next: () => {
          this.setPaymentDetails.emit({
            billingAddress: this.billingAddressForm.value,
            paymentDetails: this.paymentDetails
          });
          this.checkoutStepService.next(this.activatedRoute);
        },
        error: () => {
          this.billingAddressForm.get('documentIdentifier').setValidators([CustomValidators.aetValidator]);
          this.billingAddressForm.get('firstName').setValidators([CustomValidators.aetValidator]);
          this.billingAddressForm.get('firstName').updateValueAndValidity();
          this.billingAddressForm.get('documentIdentifier').updateValueAndValidity();
          this.billingAddressForm.controls.documentIdentifier.markAsTouched();
          this.billingAddressForm.controls.firstName.markAsTouched();
        }
      }));
    } else {
      this.billingAddressForm.markAllAsTouched();
    }
  }

  ngOnInit(): void {
    this.countries$ = this.userPaymentService.getAllBillingCountries().pipe(
      tap((countries) => {
        if (Object.keys(countries).length === 0) {
          this.userPaymentService.loadBillingCountries();
        }
      })
    );

    this.shippingAddress$ = this.checkoutDeliveryAddressFacade
      .getDeliveryAddressState()
      .pipe(
        filter((state) => !state.loading),
        map((state) => state.data)
      );

    this.showSameAsShippingAddressCheckbox$ = combineLatest([
      this.countries$,
      this.shippingAddress$,
    ]).pipe(
      map(([countries, address]) => {
        return (
          address?.country &&
          !!countries.filter(
            (country: Country): boolean =>
              country.isocode === address.country.isocode
          ).length
        );
      }),
      tap((shouldShowCheckbox) => {
        this.sameAsShippingAddress = shouldShowCheckbox;
      })
    );

    this.regions$ = this.selectedCountry$.pipe(
      switchMap((country) => this.userAddressService.getRegions(country)),
      tap((regions: Region[]) => {
        const regionControl = this.billingAddressForm.get('region.isocode');
        if (regions && regions.length > 0) {
          regionControl.enable();
        } else {
          regionControl.disable();
        }
      })
    );
  }

  openSuggestedAddress(results: AddressValidation): void {
    this.launchDialogService.openDialogAndSubscribe(
      LAUNCH_CALLER.SUGGESTED_ADDRESSES,
      undefined,
      {
        enteredAddress: this.billingAddressForm.value,
        suggestedAddresses: results.suggestedAddresses,
      }
    );
  }

  close(): void {
    this.closeForm.emit();
  }

  back(): void {
    this.goBack.emit();
  }

  verifyAddress(): void {
    if (this.sameAsShippingAddress) {
      this.next();
    } else {
      this.customCheckoutDeliveryAddressService.verifyAddress(this.billingAddressForm.value);
    }
  }

  countrySelected(country: Country): void {
    this.billingAddressForm.get('country.isocode').setValue(country.isocode);
    this.selectedCountry$.next(country.isocode);
  }

  ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
    if (this.addressVerifySub) {
      this.addressVerifySub.unsubscribe();
    }
  }
}
